import * as React from "react";
import { forwardRef, useRef } from "react";
import { useBoolState } from "@dvrt/react-tools";
import { Box, Button, Dialog, DialogContent, Divider, ListItemText, Menu, MenuItem, useTheme } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useFileUploader } from "conference/components/meeting-page/room/fileUploader";
import { DenseListItemIcon } from "conference/components/widgets";
import { DialogActions, DialogTitle } from "conference/components/dialogs";
import { formatBytes } from "conference/components/meeting-page/room/sidebar/chat/tools";
import { FormattedMessage } from "react-intl";

const FileUploadMenuItem = forwardRef(
    ({ close, onBeforeUpload }: { close: () => void; onBeforeUpload: (file: File) => boolean }, _ref) => {
        const inputRef = useRef<HTMLInputElement>(null);
        const { upload, enabled } = useFileUploader();

        const onClick = () => {
            inputRef.current.click();
            close();
        };

        const onFileInputChange = () => {
            if (inputRef.current.files?.length && enabled) {
                const file = inputRef.current.files[0];
                if (onBeforeUpload(file)) upload(file);
                inputRef.current.value = "";
            }
        };

        return (
            <MenuItem dense onClick={onClick} disabled={!enabled}>
                <DenseListItemIcon>
                    <FontAwesomeIcon icon="upload" />
                </DenseListItemIcon>
                <ListItemText>
                    <FormattedMessage id="meetingPage.room.sidebar.chat.uploadFile" defaultMessage="Upload a file" />
                    <input type="file" style={{ display: "none" }} ref={inputRef} onChange={onFileInputChange} />
                </ListItemText>
            </MenuItem>
        );
    },
);

const useFileSizeChecker = () => {
    const [isShowSizeErrorDialog, showSizeErrorDialog, hideSizeErrorDialog] = useBoolState();
    const { maxSize } = useFileUploader();

    const onBeforeUpload = (file: File) => {
        if (file.size < maxSize) return true;

        showSizeErrorDialog();

        return false;
    };

    return { onBeforeUpload, isShowSizeErrorDialog, hideSizeErrorDialog };
};

const SizeErrorDialog = ({ isShow, hide }: { isShow: boolean; hide: () => void }) => {
    const { maxSize } = useFileUploader();

    return (
        <Dialog open={isShow} onClose={hide}>
            <DialogTitle>
                <FormattedMessage
                    id="meetingPage.room.sidebar.chat.fileUploadError.size.title"
                    defaultMessage="The file is too big!"
                />
            </DialogTitle>
            <DialogContent sx={{ padding: 2 }}>
                <FormattedMessage
                    id="meetingPage.room.sidebar.chat.fileUploadError.size.content"
                    defaultMessage="The maximum file size is {size}"
                    values={{ size: formatBytes(maxSize) }}
                />
            </DialogContent>
            <Divider />
            <DialogActions>
                <Button onClick={hide} color="primary">
                    <FormattedMessage id="ok" defaultMessage="OK" />
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const InputMenu = () => {
    const [isShowMenu, showMenu, hideMenu] = useBoolState();
    const buttonRef = useRef<HTMLDivElement>(null);
    const theme = useTheme();
    const { hideSizeErrorDialog, isShowSizeErrorDialog, onBeforeUpload } = useFileSizeChecker();

    return (
        <React.Fragment>
            <Box
                sx={{
                    cursor: "pointer",
                    transition: "color 0.3s",
                    padding: "0 7px",
                    marginRight: -1,
                    "&:hover": {
                        color: theme.palette.primary.main,
                    },
                }}
                onClick={showMenu}
                ref={buttonRef}
            >
                <FontAwesomeIcon icon="ellipsis-v" />
            </Box>
            <Menu anchorEl={buttonRef.current} open={isShowMenu} onClose={hideMenu} keepMounted>
                <FileUploadMenuItem close={hideMenu} onBeforeUpload={onBeforeUpload} />
            </Menu>
            <SizeErrorDialog isShow={isShowSizeErrorDialog} hide={hideSizeErrorDialog} />
        </React.Fragment>
    );
};

export default InputMenu;
